/**
 * The route composition. **F-069 is closed** — this file could not exist until the app shell left
 * `src/components/`, and T71 moved it.
 *
 * WHY IT COULD NOT EXIST BEFORE. Every protected route nests under `AdminLayout`, so this file has
 * to import it. `src/app/` may import features, shared, domain and contracts — never legacy — and
 * the layout sat in `src/components/`, which is legacy, because its own only legacy import was
 * `useAuth` in `src/hooks/`, which in turn imported `src/config/firebase.ts`. Three moves, in that
 * order, and none of them optional: that is the chain T71 unwound, and it is why this is one PR of
 * six behaviour-neutral commits rather than one large one.
 *
 * ⚠️ TWELVE FEATURES OWN THEIR ROUTES (D5) AND FIVE SCREENS STILL DO NOT. The `<Route>` elements
 * written out below are the ones still in `src/pages/`: they have no feature to export a route
 * table from. As each migrates it exports its own `routes.tsx` and joins the spread, and the
 * literal list shrinks to nothing. Do not add a thirteenth literal route here — migrate the page.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Spinner } from '@nexvia-logistics/ui';
import { auditRoutes } from '@/features/audit/routes';
import { payoutsRoutes } from '@/features/payouts/routes';
import { broadcastRoutes } from '@/features/broadcast/routes';
import { carrierInvoicesRoutes } from '@/features/carrier-invoices/routes';
import { kycRoutes } from '@/features/kyc/routes';
import { notificationRoutes } from '@/features/notifications/routes';
import { dashboardRoutes } from '@/features/dashboard/routes';
import { syntheticRoutes } from '@/features/synthetic/routes';
import { requestsRoutes } from '@/features/requests/routes';
import { settingsRoutes } from '@/features/settings/routes';
import { clientsRoutes } from '@/features/clients/routes';
import { complaintsRoutes } from '@/features/complaints/routes';
import { driversRoutes } from '@/features/drivers/routes';
import { storageRoutes } from '@/features/storage/routes';

const Login = lazy(() => import('@/pages/Login'));
const AdminLayout = lazy(() => import('@/shared/AdminLayout'));
const ShipmentDetails = lazy(() => import('@/pages/ShipmentDetails'));
const AdminInvoicing = lazy(() => import('@/pages/invoicing'));
const Banks = lazy(() => import('@/pages/Banks'));

/** Every feature route table, in one place so the spread below reads as one thing. */
const featureRoutes = [
    ...auditRoutes,
    ...broadcastRoutes,
    ...carrierInvoicesRoutes,
    ...clientsRoutes,
    ...complaintsRoutes,
    ...dashboardRoutes,
    ...driversRoutes,
    ...kycRoutes,
    ...notificationRoutes,
    ...payoutsRoutes,
    ...requestsRoutes,
    ...settingsRoutes,
    ...storageRoutes,
    ...syntheticRoutes,
];

const PageLoader = () => (
    <div className="flex min-h-screen items-center justify-center">
        <Spinner size={40} label="Cargando" />
    </div>
);

export const AppRouter = () => (
    <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<AdminLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                {featureRoutes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                ))}
                {/* Still in `src/pages/`, so they have no feature route table to come from. */}
                <Route path="requests/:id" element={<ShipmentDetails />} />
                <Route path="invoicing" element={<AdminInvoicing />} />
                <Route path="banks" element={<Banks />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    </Suspense>
);
